import * as Sentry from '@sentry/react';

import type { ErrorContext, ErrorTracker } from '../types.ts';
import { apiConfigAccessor } from '@/stores/webStorages';

export class SentryErrorTracker implements ErrorTracker {
  init(config: { environment: string; dsn: string }) {
    Sentry.init({
      dsn: config.dsn,
      environment: config.environment,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 0.3,
      normalizeDepth: 3,
      maxBreadcrumbs: 30,
      attachStacktrace: true,
      ignoreErrors: [
        'Network request failed',
        'NetworkError',
        'Network Error',
        'Failed to fetch',
        'Load failed', // Safari/iOS equivalent of 'Failed to fetch' (transient network errors + failed dynamic imports)
        'ResizeObserver loop limit exceeded',
        'ChunkLoadError',
        'Loading chunk',
        // Safari-specific stale-chunk errors after deployment (SN-7768)
        'Importing a module script failed',
        'is not a valid JavaScript MIME type',
        'Unable to preload CSS',
        'Skipping view transition because viewport size changed',
        'Skipping view transition because skipTransition() was called.',
        'Skipped ViewTransition due to skipTransition() call',
        // Chrome reformulations of the ViewTransition skip — same cause, different phrasing per version.
        // Chrome 153 emits "Transition was skipped. skipTransition() called" (SN-7971);
        // Chrome Mobile 151 on Android emits the shorter "Transition was skipped" (SN-8129).
        // The prefix covers both and any future variants.
        'Transition was skipped',
        // Firefox Mobile on Android backgrounds the app between the visibilityState guard
        // (useFlowNavigation / AttractionsList) and React Router's internal startViewTransition()
        // call. The browser skips the animation and rejects the finished promise — navigation
        // still completes. Zero user impact. SN-7667.
        'Skipped ViewTransition due to document being hidden',
        // iOS Safari 26.6 (iOS 18.7) variant of the same race: document becomes hidden between
        // the visibilityState guard and React Router's startViewTransition() call. Navigation
        // still completes; the animation is skipped. Different phrasing, same cause. SN-8104.
        'Skipping view transition because document visibility state has become hidden.',
        // Mobile Safari 26.6 (iOS 18.7) reformulation of the same document-hidden skip — same
        // cause and zero user impact, different phrasing. SN-8082.
        'View transition was skipped because document visibility state is hidden.',
        // Firefox-specific variant of the Chrome "View transition update callback timed out" message.
        // The animation is skipped; page navigation completes normally. SN-7657.
        'Skipped ViewTransition due to timeout',
        'View transition update callback timed out',
        // Third-party runtime scripts (GTM tags, Microsoft Clarity, payment SDKs) call
        // window.webkit.messageHandlers (WKWebView native bridge) without guarding for its
        // existence. Facebook's in-app browser on iOS exposes window.webkit but not
        // messageHandlers, causing unhandled TypeErrors in scripts we cannot modify.
        // Root fix: add `window.webkit?.messageHandlers` guard in GTM-KJ6KVKV8 container.
        // Covers: SN-7415 (GTM tag), SN-7416 (Clarity), SN-7428 (setupIosCallbackHandler),
        // SN-7473 (sendDataToNative via processLargestContentfulPaintEvent).
        /window\.webkit\.messageHandlers/,
        // iOS in-app browsers (Facebook, Instagram, TikTok) inject scripts that reference
        // the bare `webkit` variable (no `window.` prefix) without guarding for its existence.
        // The existing /window\.webkit\.messageHandlers/ pattern doesn't match this form.
        "Can't find variable: webkit",
        // The Whale browser (Naver, iOS/Android) autofill traverses all frames including
        // cross-origin ones (GTM noscript iframe, Converge payment iframe). The Same-Origin
        // Policy throws SecurityError; caught by global onerror. Zero user impact — the
        // purchase flow is unaffected. SN-7550.
        /Blocked a frame with origin .* from accessing a cross-origin frame/,
        // Chrome's Web Payments infrastructure intercepts .pkpass (Apple Wallet) file
        // downloads triggered by link.click() on a blob URL and internally creates a
        // PaymentRequest session. When that session fails (no registered payment handler
        // for the origin), Chrome rejects its own internal Mojo IPC promise with this
        // plain string — entirely outside app code, uncatchable. SN-7551.
        /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
        // Facebook's Android in-app browser injects a performance-logging script
        // (navigation_performance_logger_android, iabjs:// scheme) that calls the native
        // Android Java bridge via postMessage. When the WebView is being destroyed, the
        // Java object is garbage-collected before the JS call completes. This error
        // originates entirely in Facebook's injected code — not in our application.
        // Covers: SN-7474.
        'Error invoking postMessage: Java object is gone',
        // TikTok's Android in-app browser injects a native Java bridge (invokeMethod) for
        // its SDK. When the user navigates away the Java object is GC'd, but TikTok's own
        // setTimeout callback fires after destruction and calls invokeMethod on the dead
        // object. The error originates entirely in TikTok's injected code — not in our
        // application (no app frames in the stack trace). Same teardown pattern as SN-7474.
        // Covers: SN-7740.
        'Error invoking invokeMethod: Java object is gone',
        // Android in-app browser (unidentified, Android 16 / Chrome Mobile 150) injects a
        // script that calls a native Java bridge method named "post". When the WebView
        // is torn down, the Java object is GC'd before the setTimeout callback fires and
        // Android throws "Method not found" on the dead bridge. Identical teardown pattern
        // to SN-7474 / SN-7740 — no application frames in the stack. SN-8144.
        'Error invoking post: Method not found',
        // Facebook's in-app browser injects a CAPI (Conversions API) parameter builder
        // script (capiParamBuilder/clientParamBuilder.bundle.js) that emits an internal
        // "ebp got overrides" diagnostic warning (ebp = Event Builder Protocol). Sentry's
        // global onunhandledrejection handler catches it as an unhandled promise rejection.
        // Error originates entirely in Facebook's injected code, not in our application.
        // SN-7675.
        'warning: ebp got overrides.',
        // Facebook's iOS in-app browser (WKWebView) injects attribution-tracking scripts
        // that attempt to retrieve a click ID from an internal "pool" via an async IPC call.
        // Multiple failure modes produce different suffixes: "pool fetch failed" (network/IPC
        // failure) and "click id pool not enabled" (pool feature disabled in this WebView
        // session). Both are entirely inside Facebook's injected code — no user impact, no app
        // code involved. Mechanism: auto.browser.global_handlers.onunhandledrejection.
        // Covers: SN-7668, SN-7839.
        /Fetched empty click ID because:/,
        // Google Publisher Tags (GPT/DFP) is loaded via a GTM container tag on the SN venue.
        // TikTok's in-app browser blocks the GPT script (securepubads.g.doubleclick.net/tag/js/gpt.js),
        // but a GTM tag's setInterval callback keeps polling window.googletag which never loads.
        // Zero user impact — the purchase flow is unaffected. SN-7728.
        'googletag is not defined',
        // A GTM custom HTML tag on the /attractions page uses a `start` variable (likely for
        // time-on-page or scroll tracking) inside a setInterval callback. The variable falls
        // out of scope between tag execution and interval firing, producing this ReferenceError
        // entirely in eval'd GTM code (stack: sentryWrapped → eval (3:1472), no app frames).
        // Mechanism: auto.browser.browserapierrors.setInterval. 1 event, 0 users. SN-8370.
        'start is not defined',
        // TikTok's Android in-app browser injects a script (e.g. SR5M0NKR.js at path root /)
        // that expects jQuery (window.$) to be present. Our app does not load jQuery; the
        // ReferenceError originates entirely in TikTok's injected code — no application frames
        // appear in the stack trace. Mechanism: auto.browser.global_handlers.onerror.
        // 1 event, 0 users affected. SN-7984.
        '$ is not defined',
        // Brave browser on iOS injects a compatibility script that references __firefox__, a
        // variable only available in actual Firefox environments. The script fires at global
        // scope on the /view-tickets page (stack: "at global code (/view-tickets:1:12)") and
        // is entirely outside our application code — __firefox__ does not appear in our source.
        // Mechanism: auto.browser.global_handlers.onerror. 156 events, 0 users affected. SN-8096.
        "Can't find variable: __firefox__",
        // TikTok's Android in-app browser injects SDK scripts that reference a global `serviceName`
        // variable which is never defined in our application. The error fires via window.onerror
        // from TikTok's own injected code (stack trace filename is the page URL, not a Vite asset).
        // `serviceName` does not appear anywhere in our source code. Zero user impact. SN-8040.
        'serviceName is not defined',
        // DuckDuckGo Privacy Browser (iOS/Android) injects ContentScopeScripts for its privacy-
        // protection features (e.g. fingerprintingCanvas, trackingCookies, pageContext). When the
        // browser's feature registry fails to find a registered feature — typically a timing or
        // blocking race in the extension's initialisation — it throws this error as an unhandled
        // promise rejection. The error originates entirely in DuckDuckGo's injected code;
        // no application frames are present in the stack. Zero user impact. SN-7852.
        'feature named `pageContext` was not found',
        // DuckDuckGo Privacy Browser (iOS/Android) content scripts use the WebExtensions
        // runtime.sendMessage() API to communicate with the extension background page. When
        // the tab context becomes stale during navigation or tab lifecycle transitions, the
        // runtime rejects with "Tab not found." as an unhandled promise rejection. The error
        // originates entirely in DuckDuckGo's injected code; no application frames are present
        // in the stack. Zero user impact. SN-8135.
        'Invalid call to runtime.sendMessage(). Tab not found.',
        // DuckDuckGo Privacy Browser (macOS 26.6) uses WKWebView and injects ContentScopeScripts
        // that postMessage to the native WKWebView bridge for privacy-feature communication.
        // When the native handler doesn't respond (WebView teardown or a timing race between the
        // JS call and WKWebView's teardown), WKWebView rejects the promise with this message.
        // Error originates entirely in DuckDuckGo's injected code — no app frames in the stack.
        // 170 events, 0 users affected. SN-8192.
        'WKWebView API client did not respond to this postMessage',
        // Sentry Session Replay SDK internally calls browser.storage.local.set() (iOS 18 Safari's
        // WebExtensions-compatible storage API) to persist compressed recording chunks. When the
        // device's localStorage is full the async write rejects; Sentry's own onunhandledrejection
        // handler then captures the rejection, creating a self-referential error with zero user
        // impact. The purchase flow is unaffected. SN-7879.
        /browser\.storage\.local\.set/,
        // Google's iOS in-app browser (Google 430.x, the Google app's WKWebView) injects
        // attribution-tracking and measurement scripts that are shipped minified. When these
        // internal scripts throw, the class or variable name appears as the error message because
        // the scripts call new Error(this.constructor.name) or similar, and the class is minified
        // to a two-character identifier. "Ba" is not present anywhere in our source code.
        // Mechanism: auto.browser.global_handlers.onunhandledrejection. 1 event, 0 users.
        // Regex anchored to the exact two-character message to avoid silencing any real errors.
        // SN-7863.
        /^Ba$/,
        // Google's iOS in-app browser (Google 441.x, the Google app's WKWebView) — same pattern
        // as SN-7863 above: a minified attribution/analytics script calls new Error(this.constructor.name)
        // and the minified class name "La" becomes the error message. "La" does not appear anywhere
        // in our source code as an error message. Mechanism: auto.browser.global_handlers.onunhandledrejection.
        // 1 event, 0 users affected. SN-8952.
        /^La$/,
        // Google's iOS in-app browser (Google 433.x, the Google app's WKWebView) injects a
        // text-resizing script via the `gmo` (Google Mobile Object) namespace. On iOS 26.6.0,
        // `gmo.webView` is undefined when the resize script fires, producing this TypeError in
        // Google's own injected code. The `gmo` identifier does not appear anywhere in our
        // source code. Mechanism: auto.browser.global_handlers.onerror. 1 event, 0 users.
        // SN-8015.
        /gmo\.webView\.textResize/,
        // On WebKit-based iOS browsers (Brave, Chrome, Safari forks) that carry Firefox-
        // compatibility layers or privacy-feature scripts, window.__firefox__ is undefined.
        // Multiple browser-injected scripts access properties on this object, each producing
        // "undefined is not an object (evaluating 'window.__firefox__.<property>')" at global
        // scope (stack: "at global code (/<route>:1:19)") — browser-injected lines, never our
        // bundle. window.__firefox__ does not appear anywhere in our source code.
        // Known variants:
        //   .reader        — Firefox Reader Mode availability check. SN-8097. 132 events, 0 users.
        //   .refresh_youtube_quality_<hash> — YouTube quality enhancer (Brave/iOS). SN-8858. 7 events, 0 users.
        /window\.__firefox__\./,
        // Elavon Converge PayWithConverge.js (loaded globally in index.html) has an internal
        // idle-session timer that fires a rejected Promise when the hosted-payment lightbox
        // sits idle too long. The rejection bubbles to window.onunhandledrejection before any
        // app code can intercept it — Converge's SDK does not expose a catch hook. The purchase
        // flow is unaffected (the user's session simply expired). Zero user impact. SN-8373.
        'JS idle timeout exceeded',
      ],
      denyUrls: [
        /extensions\//i,
        /^chrome:\/\//i,
        /^moz-extension:\/\//i,
        /\/gtag\//i,
        /googletagmanager\.com/i,
        // Facebook Pixel (loaded via GTM) uses `instanceof` against native constructors that
        // Sentry Replay proxies (e.g. setTimeout). The proxy is not a real constructor so RHS
        // of instanceof is not an object — error is inside fbevents.js, unrelated to our code.
        // SN-7476
        /connect\.facebook\.net/i,
        // iabjs:// is Facebook's in-app browser protocol for injected scripts (e.g. navigation_performance_logger_android).
        // When their Java postMessage bridge throws on Android teardown, Sentry captures it as an unhandled error from our page.
        // SN-7471: Android equivalent of the iOS webkit.messageHandlers filter above.
        /^iabjs:\/\//i,
        // Facebook's CAPI parameter builder (capiParamBuilder/clientParamBuilder.bundle.js) is
        // injected by Facebook's in-app browser and served from Facebook's CDN (not connect.facebook.net).
        // Belt-and-suspenders alongside the ignoreErrors entry for 'warning: ebp got overrides.'. SN-7675.
        /capiParamBuilder/i,
        // Microsoft Clarity (CDN-served, version not pinned) has an internal bug in its generator/iterator
        // where a null is returned where a sequence-bearing object is expected. The error originates
        // entirely within clarity.js — no application code in the stack. SN-7669.
        /clarity\.ms/i,
        // OneTrust Cookie Compliance SDK (otSDKStub.js, injected at CDN level — not in our codebase)
        // makes an XHR request to fetch consent configuration. Under certain network conditions
        // (CDN error, TikTok in-app browser quirks) the response is an XML error page instead of JSON.
        // OneTrust calls JSON.parse() without a try-catch, producing a SyntaxError. Zero user impact
        // (0 affected users, 11 events). Entirely in third-party code we cannot modify. SN-7985.
        /otSDKStub\.js/i,
        // A GTM-loaded ad pixel script (/ads/pixel.js) on the /attractions page enters an infinite
        // setInterval recursion loop on Firefox 153 (Windows), producing InternalError: too much
        // recursion. The entire stack trace is inside the pixel script — no application frames are
        // present. Mechanism: auto.browser.browserapierrors.setInterval. 1 event, 0 users affected.
        // SN-8137.
        /\/ads\/pixel\.js/i,
      ],

      beforeSend(event) {
        if (event.exception?.values?.[0]?.stacktrace?.frames) {
          const frames = event.exception.values[0].stacktrace.frames;
          if (frames.some((frame) => frame.filename?.includes('extension://'))) {
            return null;
          }
          // Instagram's in-app browser injects a script at path "/attractions" (not a Vite
          // asset — our own built chunk is "/assets/attractions-root-[hash].js") that uses
          // MutationObserver to detect login fields and bridges back to the Instagram native
          // app via postMessage. On iOS 26 WebKit, calling postMessage on a restricted object
          // throws InvalidAccessError (DOMException code 15) inside Instagram's code — not ours.
          // Checked here (not denyUrls) so we match only frames from the injected script,
          // never suppressing errors from our own /attractions application route. SN-7660.
          if (frames.some((frame) => /^(https?:\/\/[^/]+)?\/attractions$/.test(frame.filename ?? ''))) {
            return null;
          }

          // Android Chrome proxies external scripts through /__av/<base64-url>, so
          // denyUrls regexes (which match against raw filenames) miss GTM/Facebook frames.
          // Decode and re-check against the same patterns. SN-7670.
          const hasProxiedDeniedFrame = frames.some((frame) => {
            const filename = frame.filename ?? '';
            if (!filename.startsWith('/__av/')) return false;
            try {
              const decoded = atob(filename.slice(6));
              return /googletagmanager\.com|connect\.facebook\.net/i.test(decoded);
            } catch {
              return false;
            }
          });
          if (hasProxiedDeniedFrame) return null;
        }

        const errorMessage = event.message || event.exception?.values?.[0]?.value || '';
        if (errorMessage.includes('aborted') || errorMessage.includes('cancelled')) {
          return null;
        }

        // Belt-and-suspenders: ignoreErrors occasionally misses this on iOS Facebook browser
        // (WKWebView partial webkit exposure). Covered by SN-7428, SN-7473.
        if (errorMessage.includes('webkit.messageHandlers')) {
          return null;
        }

        // Belt-and-suspenders: ignoreErrors misses 'Error invoking *' Android Java bridge
        // teardown variants when they fire via setTimeout (Sentry captures them through a
        // different internal path). Seen variants: postMessage (SN-7474), invokeMethod (SN-7740),
        // post (SN-8144). Guard is gated on no in_app frames — if our code is ever on the
        // stack this check passes through to the normal pipeline.
        if (
          errorMessage.includes('Error invoking') &&
          !event.exception?.values?.[0]?.stacktrace?.frames?.some((f) => f.in_app)
        ) {
          return null;
        }

        // Facebook's iOS in-app browser (WKWebView) injects scripts that call postMessage
        // on a restricted or detached window object, throwing InvalidAccessError (DOMException
        // code 15). The stack has only Sentry's wrapper + native postMessage — no application
        // code is involved. iOS counterpart of the Android Java-object-gone filter. SN-7662.
        if (
          errorMessage.includes('does not support the operation or argument') &&
          !event.exception?.values?.[0]?.stacktrace?.frames?.some((f) => f.in_app)
        ) {
          return null;
        }
        // Instagram's in-app browser (iOS) injects a JS bridge that watches for login fields via
        // MutationObserver. When it detects a change, it calls postMessage on the native bridge
        // target — which throws InvalidAccessError (DOMException code 15) if the target is
        // unavailable. The error originates entirely in Instagram's injected code. SN-7659.
        const INSTAGRAM_BRIDGE_FUNS = ['logLoginFieldDetected', 'dispatchToBridge', 'sendPostMessage'];
        if (
          event.exception?.values?.[0]?.stacktrace?.frames?.some((frame) =>
            INSTAGRAM_BRIDGE_FUNS.includes(frame.function ?? ''),
          )
        ) {
          return null;
        }

        // DuckDuckGo Privacy Browser (iOS) ContentScopeScripts perform origin validation for
        // their privacy-protection features. When validation fails — typically a timing race
        // during script initialisation — the script rejects with the bare string "invalid origin"
        // as an unhandled promise rejection. No application frames are present; zero user impact.
        // Companion to SN-7852's "feature named `pageContext` was not found" filter. SN-8198.
        if (
          errorMessage === 'invalid origin' &&
          !event.exception?.values?.[0]?.stacktrace?.frames?.some((f) => f.in_app)
        ) {
          return null;
        }

        // Belt-and-suspenders: ignoreErrors misses SecurityErrors thrown inside the Sentry
        // Replay SDK itself. The Replay SDK's onIframeLoad handler accesses
        // iframe.contentWindow.Element to check same-origin status; cross-origin GTM noscript
        // iframes trigger a SecurityError that is captured via Sentry's internal path,
        // bypassing the ignoreErrors pipeline. SN-7617.
        if (
          errorMessage.includes('Blocked a frame with origin') &&
          errorMessage.includes('from accessing a cross-origin frame')
        ) {
          return null;
        }

        return event;
      },
    });

    Sentry.setContext('build', {
      version: document.querySelector<HTMLMetaElement>('meta[name="build-version"]')?.content,
      branch: document.querySelector<HTMLMetaElement>('meta[name="build-branch"]')?.content,
    });

    // Set prefers-reduced-motion as a global context
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    Sentry.setContext('accessibility', {
      prefersReducedMotion,
    });
    Sentry.setContext('appSettings', {
      config: apiConfigAccessor.getConfig(),
    });
  }

  captureException(error: Error | unknown, context?: ErrorContext) {
    const scopeData: { extra: Record<string, any> } = { extra: {} };

    scopeData.extra.isSnDomainAvailable = Boolean(window.SNDomain);
    scopeData.extra.detectedVenue = scopeData.extra.isSnDomainAvailable ? window.SNDomain.detectVenue() : null;

    if (context?.extra) {
      scopeData.extra = context.extra;
    }

    Sentry.captureException(error, scopeData);
  }

  captureMessage(message: string, level: 'info' | 'warning' | 'error', context?: ErrorContext) {
    const scopeData: { level: 'info' | 'warning' | 'error'; extra: Record<string, any> } = { level, extra: {} };

    scopeData.extra.isSnDomainAvailable = Boolean(window.SNDomain);
    scopeData.extra.detectedVenue = scopeData.extra.isSnDomainAvailable ? window.SNDomain.detectVenue() : null;

    if (context?.extra) {
      scopeData.extra = context.extra;
    }

    Sentry.captureMessage(message, scopeData);
  }
}
